/**
 * The bars of one part, laid out from the voices of its staves.
 *
 * A bar keeps its voices in one list, and a tie is followed from bar to bar by a voice's
 * place in that list (`document/validate-links.ts`, `playback/schedule-part.ts`). So where
 * a voice is put matters: a note tied over the bar line must be at the same place on both
 * sides of it, or the tie joins nothing. Each staff's first voice comes first, staff by
 * staff; those are in every bar, so they never move. The further voices come after them,
 * and one that is tied in from the bar before stays at the place it had there. The others
 * take the places left, those tied on into the next bar first, since they are the ones
 * that will have to stay. Where that still leaves a place empty before a voice that could
 * not move, it is written as a bar's rest.
 */

import { voice } from "../document/create";
import type { MusicEvent, Voice } from "../document/types";
import { barRest } from "./written";

/** One voice of one staff: the events of each bar, or nothing where it is silent. */
export type VoiceBars = readonly (readonly MusicEvent[] | null)[];

/** A further voice of one bar: whose it is, what it plays, and whether it is tied on. */
interface Further {
  readonly id: string;
  readonly staff: number;
  readonly events: readonly MusicEvent[];
  readonly tied: boolean;
}

function tiedOver(events: readonly MusicEvent[]): boolean {
  const last = events[events.length - 1];
  return last?.kind === "note" && last.pitches.some((pitch) => pitch.tieStart === true);
}

/** The further voices sounding in one bar, staff by staff. */
function furtherOf(staves: readonly (readonly VoiceBars[])[], bar: number): Further[] {
  const found: Further[] = [];
  staves.forEach((voices, staff) => {
    voices.slice(1).forEach((bars, index) => {
      const events = bars[bar];
      if (events == null) return;
      found.push({ id: `${staff}:${index}`, staff, events, tied: tiedOver(events) });
    });
  });
  return found;
}

/** The staff with the fewest voices in a bar, the upper of two alike: where a rest is least in the way. */
function emptiest(count: number, further: readonly Further[]): number {
  let staff = 0;
  let least = Infinity;
  for (let candidate = 0; candidate < count; candidate += 1) {
    const voices = further.filter((one) => one.staff === candidate).length;
    if (voices < least) {
      least = voices;
      staff = candidate;
    }
  }
  return staff;
}

/** The voices of every bar of a part whose staves are written in the voices given. */
export function barsOf(staves: readonly (readonly VoiceBars[])[], count: number): Voice[][] {
  const out: Voice[][] = [];
  // Where each further voice tied over the last bar line stands, counted past the firsts.
  let kept = new Map<string, number>();
  for (let bar = 0; bar < count; bar += 1) {
    const further = furtherOf(staves, bar);
    const places = new Map<number, Further>();
    for (const one of further) {
      const at = kept.get(one.id);
      if (at !== undefined) places.set(at, one);
    }
    const free = further
      .filter((one) => !kept.has(one.id))
      .sort((first, second) => Number(second.tied) - Number(first.tied));
    let at = 0;
    for (const one of free) {
      while (places.has(at)) at += 1;
      places.set(at, one);
    }

    const written = staves.map((voices, staff) => voice(staff, voices[0]?.[bar] ?? barRest()));
    const last = Math.max(-1, ...places.keys());
    for (let place = 0; place <= last; place += 1) {
      const one = places.get(place);
      written.push(
        one === undefined
          ? voice(emptiest(staves.length, further), barRest())
          : voice(one.staff, one.events),
      );
    }
    out.push(written);
    kept = new Map(
      [...places.entries()].filter(([, one]) => one.tied).map(([place, one]) => [one.id, place]),
    );
  }
  return out;
}
